import { Directive, inject, OnDestroy, OnInit, output } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { CrudAbstractModel } from "./crud-abstract.model";
import { CrudAbstractRepository } from "./crud-abstract.repository";
import { Subscription } from "rxjs";
import { NotifyService } from "../notify/notify.service";
import { eventType } from "../notify/event.const";
import { TOAST } from "../notify/toast/toast-type.enum";
import { BackendErrorPipe } from "../shared/backend/backend-error.pipe";
import { ActivatedRoute, Router } from "@angular/router";
import { EventService } from "../shared/event/event.service";

@Directive()
export abstract class CrudAbstractFormComponent implements OnInit, OnDestroy {
  protected repository: CrudAbstractRepository;
  protected formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
  protected notifyService: NotifyService = inject(NotifyService);
  protected eventService: EventService = inject(EventService);
  protected router: Router = inject(Router);
  protected route: ActivatedRoute = inject(ActivatedRoute);

  public model: CrudAbstractModel;
  public form: UntypedFormGroup;
  public loading = false;

  readonly modelChange = output<CrudAbstractModel>();

  protected subscription = new Subscription();

  ngOnInit() {
    this.setModelFromResolve();
    this.scrollToTop();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public abstract prepareSaveModel(form: UntypedFormGroup): CrudAbstractModel;

  public abstract createForm(): UntypedFormGroup;

  // TODO: Can be removed?
  public saveOrAdd(shouldClose = false) {
    if (!this.model.getId()) {
      this.add(this.form);
    }

    if (this.model.getId()) {
      this.save(this.form, shouldClose);
    }
  }

  // TODO: first param can be removed
  public add(form: UntypedFormGroup) {
    this.loading = true;
    this.model = this.prepareSaveModel(this.form);
    this.subscription.add(
      this.repository.add(this.model).subscribe({
        next: (response): void => {
          const map = this.repository.getMapper();
          const model = map(response);
          if (!model.getId()) {
            return;
          }
          this.model = model;
          this.form.patchValue(this.model);
          this.eventService.eventBus.emit({
            event: eventType.itemCreated,
            data: this.model,
          });
          this.loading = false;
          this.modelChange.emit(model);
          window.history.replaceState(
            {},
            "",
            `/${this.repository.getEndpoint()}/${this.model.getId()}`,
          );
        },
        error: (err): void => {
          this.notifyService.toast(
            TOAST.ERROR,
            "Can't add this item",
            new BackendErrorPipe().transform(err, "string"),
          );
          this.loading = false;
        },
      }),
    );
  }

  // TODO: first param can be removed
  public save(form: UntypedFormGroup, shouldClose = false) {
    this.loading = true;
    this.model = this.prepareSaveModel(this.form);
    if (this.model.getId()) {
      this.subscription.add(
        this.repository.save(this.model).subscribe({
          next: (response): void => {
            const map = this.repository.getMapper();
            const model = map(response);
            this.model = model;
            this.form.patchValue(this.model);
            this.eventService.eventBus.emit({
              event: eventType.itemUpdated,
              data: this.model,
            });
            this.modelChange.emit(model);
            if (shouldClose) {
              this.close();
            }
            this.loading = false;
          },
          error: (err): void => {
            this.notifyService.toast(
              TOAST.ERROR,
              "Error",
              new BackendErrorPipe().transform(err, "string"),
            );
            this.loading = false;
          },
        }),
      );
    }
  }

  public delete(model: CrudAbstractModel): void {
    this.notifyService
      .confirm("Are you sure you want to delete this element?", "Delete", {
        destructive: true,
      })
      .subscribe(() => {
        this.loading = true;
        this.subscription.add(
          this.repository.delete(model).subscribe({
            next: (response): void => {
              this.eventService.eventBus.emit({
                event: eventType.itemDeleted,
                data: model,
              });
              this.close();
              this.loading = false;
            },
            error: (error): void => {
              this.notifyService.toast(
                TOAST.ERROR,
                <string>new BackendErrorPipe().transform(error),
              );
              this.loading = false;
            },
          }),
        );
      });
  }

  public close() {
    if (history.length > 1) {
      history.back();
    } else {
      this.router.navigate([`margin-rule/list`]);
    }
  }

  protected setModelFromResolve() {
    this.model = this.getModelResolve()
      ? this.getModelResolve()
      : this.prepareSaveModel(new UntypedFormGroup({}));
    this.form = this.createForm();
  }

  protected getModelResolve(): CrudAbstractModel {
    return this.route.data["value"]["model"];
  }

  private scrollToTop() {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }
}
